import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ragAPI, visaAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import './RAGDocuments.css';

const RAGDocuments = () => {
  // Documents State
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);

  // Filters
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [countryDropdownOpen, setCountryDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const countryDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  // Stats
  const [stats, setStats] = useState({
    totalDocuments: 0,
    indexedDocuments: 0,
    totalChunks: 0,
    pendingDocuments: 0
  });

  // Modal State
  const [uploadModal, setUploadModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false });
  const [toast, setToast] = useState(null);

  // Upload State
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadCountry, setUploadCountry] = useState(null);
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef(null);

  const categories = [
    { value: 'visa_guide', label: 'Visa Guide' },
    { value: 'travel_advisory', label: 'Travel Advisory' },
    { value: 'faq', label: 'FAQ' },
    { value: 'general', label: 'General' },
    { value: 'pricing', label: 'Pricing' }
  ];

  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'processing', label: 'Processing' },
    { value: 'indexed', label: 'Indexed' },
    { value: 'failed', label: 'Failed' }
  ];

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch Countries
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await visaAPI.getCountries();
        if (response.data.success) {
          setCountries(response.data.data.countries);
        }
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      }
    };
    fetchCountries();
  }, []);

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setCountryDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch Documents
  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (selectedCountry) params.country_id = selectedCountry.id;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedStatus) params.status = selectedStatus;

      const response = await ragAPI.getDocuments(params);
      if (response.data.success) {
        setDocuments(response.data.data.documents);
        setStats({
          totalDocuments: response.data.data.total || response.data.data.documents.length,
          indexedDocuments: response.data.data.documents.filter(d => d.status === 'indexed').length,
          totalChunks: response.data.data.documents.reduce((acc, d) => acc + (d.chunk_count || 0), 0),
          pendingDocuments: response.data.data.documents.filter(d => d.status === 'pending' || d.status === 'processing').length
        });
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      showToast('Failed to load documents', 'error');
    } finally {
      setLoading(false);
    }
  }, [selectedCountry, selectedCategory, selectedStatus]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setUploadError('Only PDF files are allowed');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setUploadError('File size must be less than 10MB');
        return;
      }
      setUploadFile(file);
      setUploadError('');
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      setUploadError('Please select a file');
      return;
    }

    try {
      setUploading(true);
      setUploadError('');

      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('category', uploadCategory);
      if (uploadCountry) {
        formData.append('country_id', uploadCountry.id);
      }

      await ragAPI.uploadDocument(formData);

      setUploadModal(false);
      setUploadFile(null);
      setUploadCountry(null);
      setUploadCategory('general');
      showToast('Document uploaded successfully. Indexing in progress.');
      fetchDocuments();
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadError(error.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = (doc) => {
    setConfirmModal({
      show: true,
      title: 'Delete Document',
      message: `Are you sure you want to delete "${doc.original_filename}"? This will also remove all indexed chunks from the knowledge base.`,
      onConfirm: async () => {
        try {
          await ragAPI.deleteDocument(doc.id);
          showToast('Document deleted successfully');
          fetchDocuments();
        } catch (error) {
          console.error('Delete failed:', error);
          showToast('Failed to delete document', 'error');
        }
        setConfirmModal({ show: false });
      }
    });
  };

  const handleReindex = async (doc) => {
    try {
      await ragAPI.reindexDocument(doc.id);
      showToast('Re-indexing started');
      fetchDocuments();
    } catch (error) {
      console.error('Reindex failed:', error);
      showToast('Failed to start re-indexing', 'error');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'indexed': return 'rag-status-indexed';
      case 'processing': return 'rag-status-processing';
      case 'pending': return 'rag-status-pending';
      case 'failed': return 'rag-status-failed';
      default: return '';
    }
  };

  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">RAG DOCUMENTS</h1>
            <p className="page-subtitle">MANAGE KNOWLEDGE BASE FOR AI AGENT</p>
          </div>
          <div className="header-right">
            {/* Country Filter */}
            <div className="custom-dropdown" ref={countryDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setCountryDropdownOpen(!countryDropdownOpen)}
              >
                <span>{selectedCountry ? selectedCountry.name : 'All Countries'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {countryDropdownOpen && (
                <div className="custom-dropdown-menu custom-dropdown-menu-scroll">
                  <div
                    className={`custom-dropdown-item ${!selectedCountry ? 'active' : ''}`}
                    onClick={() => { setSelectedCountry(null); setCountryDropdownOpen(false); }}
                  >
                    All Countries
                  </div>
                  {countries.map((country) => (
                    <div
                      key={country.id}
                      className={`custom-dropdown-item ${selectedCountry?.id === country.id ? 'active' : ''}`}
                      onClick={() => { setSelectedCountry(country); setCountryDropdownOpen(false); }}
                    >
                      {country.name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Category Filter */}
            <div className="custom-dropdown" ref={categoryDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
              >
                <span>{selectedCategory ? getCategoryLabel(selectedCategory) : 'All Categories'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {categoryDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <div
                    className={`custom-dropdown-item ${!selectedCategory ? 'active' : ''}`}
                    onClick={() => { setSelectedCategory(null); setCategoryDropdownOpen(false); }}
                  >
                    All Categories
                  </div>
                  {categories.map((cat) => (
                    <div
                      key={cat.value}
                      className={`custom-dropdown-item ${selectedCategory === cat.value ? 'active' : ''}`}
                      onClick={() => { setSelectedCategory(cat.value); setCategoryDropdownOpen(false); }}
                    >
                      {cat.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="custom-dropdown" ref={statusDropdownRef}>
              <button
                className="custom-dropdown-trigger"
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
              >
                <span>{selectedStatus ? statusOptions.find(s => s.value === selectedStatus)?.label : 'All Status'}</span>
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="custom-dropdown-menu">
                  <div
                    className={`custom-dropdown-item ${!selectedStatus ? 'active' : ''}`}
                    onClick={() => { setSelectedStatus(null); setStatusDropdownOpen(false); }}
                  >
                    All Status
                  </div>
                  {statusOptions.map((status) => (
                    <div
                      key={status.value}
                      className={`custom-dropdown-item ${selectedStatus === status.value ? 'active' : ''}`}
                      onClick={() => { setSelectedStatus(status.value); setStatusDropdownOpen(false); }}
                    >
                      {status.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="btn-primary" onClick={() => setUploadModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17 8 12 3 7 8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
              UPLOAD PDF
            </button>
          </div>
        </div>

        <div className="dashboard-content">
          {/* Stats Cards */}
          <div className="rag-stats-grid">
            <div className="stat-card">
              <div className="stat-info">
                <span className="stat-label">TOTAL DOCUMENTS</span>
                <span className="stat-value">{stats.totalDocuments}</span>
              </div>
            </div>
            <div className="stat-card stat-success">
              <div className="stat-info">
                <span className="stat-label">INDEXED</span>
                <span className="stat-value">{stats.indexedDocuments}</span>
              </div>
            </div>
            <div className="stat-card stat-primary">
              <div className="stat-info">
                <span className="stat-label">TOTAL CHUNKS</span>
                <span className="stat-value">{stats.totalChunks}</span>
              </div>
            </div>
            <div className="stat-card stat-warning">
              <div className="stat-info">
                <span className="stat-label">PENDING</span>
                <span className="stat-value">{stats.pendingDocuments}</span>
              </div>
            </div>
          </div>

          {/* Documents Table */}
          <div className="dashboard-section rag-table-section">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading documents...</p>
              </div>
            ) : documents.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                  <polyline points="14 2 14 8 20 8"></polyline>
                </svg>
                <p>No documents uploaded yet</p>
                <button className="btn-primary" onClick={() => setUploadModal(true)}>
                  Upload First Document
                </button>
              </div>
            ) : (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>DOCUMENT</th>
                      <th>COUNTRY</th>
                      <th>CATEGORY</th>
                      <th>SIZE</th>
                      <th>CHUNKS</th>
                      <th>STATUS</th>
                      <th>INDEXED AT</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td>
                          <div className="rag-doc-info">
                            <div className="rag-doc-avatar">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                <polyline points="14 2 14 8 20 8"></polyline>
                              </svg>
                            </div>
                            <div className="rag-doc-details">
                              <span className="rag-doc-name">{doc.original_filename}</span>
                              <span className="rag-doc-pages">{doc.page_count || 0} pages</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="rag-country">{doc.country_name || 'Global'}</span>
                        </td>
                        <td>
                          <span className="rag-category-badge">{getCategoryLabel(doc.category)}</span>
                        </td>
                        <td>
                          <span className="rag-file-size">{formatFileSize(doc.file_size_bytes)}</span>
                        </td>
                        <td>
                          <span className="rag-chunk-count">{doc.chunk_count || 0}</span>
                        </td>
                        <td>
                          <span className={`rag-status ${getStatusBadgeClass(doc.status)}`}>
                            {doc.status?.toUpperCase()}
                          </span>
                        </td>
                        <td>
                          <span className="rag-date">{formatDate(doc.indexed_at)}</span>
                        </td>
                        <td>
                          <div className="rag-actions">
                            {(doc.status === 'failed' || doc.status === 'indexed') && (
                              <button
                                className="btn-action-rag btn-action-reindex"
                                onClick={() => handleReindex(doc)}
                              >
                                REINDEX
                              </button>
                            )}
                            <button
                              className="btn-action-rag btn-action-delete"
                              onClick={() => handleDelete(doc)}
                            >
                              DELETE
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Upload Modal */}
        {uploadModal && (
          <div className="modal-overlay" onClick={() => !uploading && setUploadModal(false)}>
            <div className="upload-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2 className="modal-title">Upload PDF Document</h2>
                <button
                  className="modal-close"
                  onClick={() => !uploading && setUploadModal(false)}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
              <div className="modal-body">
                {/* File Upload */}
                <div
                  className={`file-upload-zone ${uploadFile ? 'has-file' : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  {uploadFile ? (
                    <div className="file-selected">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      <span>{uploadFile.name}</span>
                      <span className="file-size-text">({formatFileSize(uploadFile.size)})</span>
                    </div>
                  ) : (
                    <div className="file-placeholder">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>Click to select PDF file</span>
                      <span className="upload-hint">Max size: 10MB</span>
                    </div>
                  )}
                </div>

                {/* Country & Category */}
                <div className="upload-form-row">
                  <div className="upload-form-group">
                    <label>Country (Optional)</label>
                    <select
                      value={uploadCountry?.id || ''}
                      onChange={(e) => {
                        const country = countries.find(c => c.id === parseInt(e.target.value));
                        setUploadCountry(country || null);
                      }}
                    >
                      <option value="">Global (All Countries)</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="upload-form-group">
                    <label>Category *</label>
                    <select
                      value={uploadCategory}
                      onChange={(e) => setUploadCategory(e.target.value)}
                    >
                      {categories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {uploadError && (
                  <div className="upload-error">{uploadError}</div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-upload-cancel"
                  onClick={() => setUploadModal(false)}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-upload-submit"
                  onClick={handleUpload}
                  disabled={uploading || !uploadFile}
                >
                  {uploading ? 'Uploading...' : 'Upload & Index'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Confirm Modal */}
        {confirmModal.show && (
          <ConfirmModal
            isOpen={true}
            title={confirmModal.title}
            message={confirmModal.message}
            confirmText="Delete"
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal({ show: false })}
          />
        )}
      </main>
    </div>
  );
};

export default RAGDocuments;
